/**
 * Iter 196 · Global SR chime.
 *
 * Plays a short mp3 whenever a NEW customer-made service request
 * arrives, no matter which admin/staff page the user is on. Suppressed
 * on the SR inbox route (which has its own audible ping to avoid a
 * double-chime).
 *
 * Poll cadence: 25s. Uses a Set of seen ids across polls, so a chime
 * only fires for GENUINELY new rows, not for the first load.
 */
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const CHIME_URL = "/sounds/sr-inbox-notify.mp3";
const POLL_MS = 25000;
const CHIME_DISABLED_KEY = "rescigo:sr_chime_disabled";
// Roles that should get audible SR pings globally.
const CHIME_ROLES = new Set([
  "superadmin", "admin", "manager", "service_coordinator",
  "office_staff", "office",
]);

export default function GlobalSrChime() {
  const { user } = useAuth() || {};
  const { pathname } = useLocation();
  const audioRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const eligible = user && CHIME_ROLES.has(user.role);
  // Suppress on the inbox page — it fires its own chime.
  const onInboxPage = pathname.startsWith("/operations/service-requests");
  const userDisabled = typeof window !== "undefined" &&
    localStorage.getItem(CHIME_DISABLED_KEY) === "1";

  useEffect(() => {
    if (!eligible || onInboxPage || userDisabled) return;
    let alive = true;

    const tick = async () => {
      try {
        const r = await api.get("/operations/sr-inbox?view=day&lite=true");
        if (!alive) return;
        const seen = knownIdsRef.current;
        const fresh = [];
        for (const day of r.data.days || []) {
          for (const req of day.requests || []) {
            if (!firstLoadRef.current && !seen.has(req.id)) fresh.push(req);
            seen.add(req.id);
          }
        }
        firstLoadRef.current = false;
        if (fresh.length > 0 && audioRef.current) {
          try {
            audioRef.current.currentTime = 0;
            const p = audioRef.current.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } catch { /* pre-gesture — will unlock later */ }
          toast.info(
            `${fresh.length} new customer SR${fresh.length === 1 ? "" : "s"}`,
            {
              description: fresh[0].customer_name
                ? `Latest: ${fresh[0].customer_name}`
                : undefined,
              action: {
                label: "Open",
                onClick: () => {
                  window.location.href = "/operations/service-requests";
                },
              },
            },
          );
        }
      } catch { /* silent — background poll */ }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [eligible, onInboxPage, userDisabled]);

  if (!eligible || onInboxPage || userDisabled) return null;
  return (
    <audio ref={audioRef} src={CHIME_URL} preload="auto"
            data-testid="global-sr-chime-audio"/>
  );
}
